// admin/GroceryOrdersTab.jsx
// Rendered inside GroceryManagement as the "Orders" tab.

import React, { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";

import { apiRequest } from "../../fetchSetup";

const PRIMARY = "#006D77";
const LIGHT = "#E0F7FA";
const BORDER = "#B2DFDB";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "placed", label: "Placed" },
  { value: "packed", label: "Packed" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const NEXT = {
  placed: { to: "packed", label: "Mark packed" },
  packed: { to: "out_for_delivery", label: "Out for delivery" },
  out_for_delivery: { to: "delivered", label: "Mark delivered" },
};

const label = s => String(s || "").replace(/_/g, " ");

// [background, text]
const tone = s => {
  const v = String(s || "").toLowerCase();
  if (["delivered", "paid"].includes(v)) return ["#E8F5E9", "#2E7D32"];
  if (["cancelled", "failed"].includes(v)) return ["#FFEBEE", "#C62828"];
  if (["out_for_delivery", "packed", "refunded"].includes(v)) return ["#E3F2FD", "#1565C0"];
  return ["#FFF8E1", "#B45309"];
};

const StatusPill = ({ value }) => {
  const [bg, color] = tone(value);
  return (
    <Box
      sx={{
        display: "inline-block",
        bgcolor: bg,
        color,
        borderRadius: "16px",
        px: 1.4,
        py: 0.3,
        fontSize: 12,
        fontWeight: 700,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label(value)}
    </Box>
  );
};

// server sends naive UTC: treat as UTC, show in IST
const fmt = s => {
  if (!s) return "";
  const d = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(s) ? s : `${s}Z`);
  return isNaN(d)
    ? ""
    : d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "medium",
        timeStyle: "short",
      });
};

const money = n => `₹${Number(n || 0).toFixed(2)}`;

const StatCard = ({ title, value, color }) => (
  <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, boxShadow: "none", height: "100%" }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 24, color: color || "#0E2A2E" }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const Line = ({ l, r, color, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
    <Typography variant="body2" color="text.secondary">{l}</Typography>
    <Typography variant="body2" fontWeight={bold ? 800 : 600} sx={{ color }}>{r}</Typography>
  </Box>
);


function GroceryOrdersTab({ apiBase, authHeaders, showDialog }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);

  const [viewOrder, setViewOrder] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ open: false, order: null, reason: "" });

  const timer = useRef(null);

  const jsonHeaders = () => ({ ...authHeaders(), "Content-Type": "application/json" });

  // debounce the search box
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDebounced(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer.current);
  }, [search]);

  useEffect(() => setPage(0), [status, rows]);


  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page + 1, limit: rows });
      if (status) params.set("status", status);
      if (debounced) params.set("search", debounced);

      const response = await apiRequest(`${apiBase}/grocery-orders?${params}`, {
        method: "GET",
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to load orders.");

      setOrders(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      showDialog(error.message || "Failed to load orders.", "Error", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [status, debounced, page, rows]); // eslint-disable-line


  const refreshAll = () => {
    loadOrders();
  };

  // keep the open details dialog in sync after an update
  const afterUpdate = async orderId => {
    await Promise.all([loadOrders()]);
    if (viewOrder?.order_id === orderId) {
      try {
        const response = await apiRequest(`${apiBase}/grocery-orders/${orderId}`, {
          method: "GET",
          headers: authHeaders(),
        });
        const result = await response.json();
        if (response.ok && result.success) setViewOrder(result.data);
      } catch {
        setViewOrder(null);
      }
    }
  };

  const updateStatus = async (order, to, reason = "") => {
    setBusyId(order.order_id);
    try {
      const response = await apiRequest(`${apiBase}/grocery-orders/${order.order_id}/status`, {
        method: "PUT",
        headers: jsonHeaders(),
        body: JSON.stringify({ status: to, reason }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to update order.");

      await afterUpdate(order.order_id);
    } catch (error) {
      showDialog(error.message || "Failed to update order.", "Error", "error");
    } finally {
      setBusyId(null);
    }
  };

  const retryRefund = async order => {
    setBusyId(order.order_id);
    try {
      const response = await apiRequest(`${apiBase}/grocery-orders/${order.order_id}/refund`, {
        method: "PUT",
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Refund failed.");

      showDialog("Refund initiated successfully.", "Refund", "success");
      await afterUpdate(order.order_id);
    } catch (error) {
      showDialog(error.message || "Refund failed.", "Error", "error");
    } finally {
      setBusyId(null);
    }
  };

  const confirmCancel = async () => {
    const { order, reason } = cancelDialog;
    setCancelDialog({ open: false, order: null, reason: "" });
    await updateStatus(order, "cancelled", reason.trim());
  };

  const canCancel = o => ["placed", "packed"].includes(o.order_status);
  const needsRefund = o => o.order_status === "cancelled" && o.payment_status === "refund_pending";

  const ActionButtons = ({ order, dense }) => {
    const next = NEXT[order.order_status];
    const busy = busyId === order.order_id;
    return (
      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        {next && (
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => updateStatus(order, next.to)}
            sx={{ bgcolor: PRIMARY, textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#00545C" } }}
          >
            {busy ? "…" : next.label}
          </Button>
        )}
        {needsRefund(order) && (
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={busy}
            onClick={() => retryRefund(order)}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            {busy ? "…" : "Refund"}
          </Button>
        )}
        {dense && canCancel(order) && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={busy}
            onClick={() => setCancelDialog({ open: true, order, reason: "" })}
            sx={{ textTransform: "none" }}
          >
            Cancel order
          </Button>
        )}
        {!dense && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setViewOrder(order)}
            sx={{ borderColor: PRIMARY, color: PRIMARY, textTransform: "none" }}
          >
            View
          </Button>
        )}
      </Stack>
    );
  };


  return (
    <>
      {/* STATS */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={2.4}>
            <StatCard title="Today's orders" value={stats.today_orders} />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <StatCard title="Today's revenue" value={`₹${Number(stats.today_revenue).toFixed(0)}`} color="#2E7D32" />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <StatCard title="To pack" value={stats.counts?.placed ?? 0} color="#B45309" />
          </Grid>
          <Grid item xs={6} md={2.4}>
            <StatCard title="Out for delivery" value={stats.counts?.out_for_delivery ?? 0} color="#1565C0" />
          </Grid>
          <Grid item xs={12} md={2.4}>
            <StatCard
              title="Refunds pending"
              value={stats.refunds_pending}
              color={stats.refunds_pending ? "#C62828" : undefined}
            />
          </Grid>
        </Grid>
      )}

      {/* FILTERS */}
      <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, mb: 2, boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search order ID, customer name, phone or email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={8} md={4}>
              <FormControl fullWidth>
                <InputLabel>Order status</InputLabel>
                <Select value={status} label="Order status" onChange={e => setStatus(e.target.value)} style={{width:204}}>
                  {STATUS_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                      {stats && o.value ? ` (${stats.counts?.[o.value] ?? 0})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <IconButton onClick={refreshAll} sx={{ border: `1px solid ${BORDER}` }}>
                <RefreshIcon />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* TABLE */}
      <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, boxShadow: "none" }}>
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: LIGHT }}>
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Delivery to</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={28} sx={{ color: PRIMARY, my: 4 }} />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography sx={{ py: 5, color: "text.secondary" }}>No grocery orders found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map(order => {
                  const a = order.delivery_address || {};
                  return (
                    <TableRow key={order.order_id} hover>
                      <TableCell>
                        <Typography fontWeight={700}>{order.order_id}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmt(order.created_at)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>{a.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.phone}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{a.community_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Block {a.address_line1}
                          {a.address_line2 ? `, ${a.address_line2}` : ""}
                        </Typography>
                        {a.delivery_time && a.delivery_time !== "Anytime" && (
                          <Typography variant="caption" sx={{ display: "block", color: "#1565C0" }}>
                            {a.delivery_time}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>{order.items?.length || 0}</TableCell>

                      <TableCell>
                        <Typography fontWeight={700} color={PRIMARY}>{money(order.total_amount)}</Typography>
                      </TableCell>

                      <TableCell>
                        <StatusPill value={order.payment_status} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3 }}>
                          {order.payment?.method === "offline" ? "Cash on delivery" : "Online"}
                        </Typography>
                        {needsRefund(order) && order.payment?.refund_error && (
                          <Typography variant="caption" sx={{ display: "block", color: "#C62828", maxWidth: 200 }}>
                            {order.payment.refund_error}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <StatusPill value={order.order_status} />
                      </TableCell>

                      <TableCell align="right">
                        <ActionButtons order={order} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rows}
          onRowsPerPageChange={e => setRows(parseInt(e.target.value, 10))}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Card>


      {/* ORDER DETAILS */}
      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontFamily: "Sora, sans-serif", fontWeight: 800, color: PRIMARY }}>
          Order {viewOrder?.order_id}
        </DialogTitle>

        <DialogContent dividers>
          {viewOrder && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <StatusPill value={viewOrder.order_status} />
                <StatusPill value={viewOrder.payment_status} />
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                  Placed {fmt(viewOrder.created_at)}
                </Typography>
              </Stack>

              <Box>
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>Delivery</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {viewOrder.delivery_address?.full_name} · {viewOrder.delivery_address?.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {viewOrder.delivery_address?.community_name}, Block {viewOrder.delivery_address?.address_line1}
                  {viewOrder.delivery_address?.address_line2 ? `, Flat ${viewOrder.delivery_address.address_line2}` : ""}
                  {viewOrder.delivery_address?.pincode ? ` - ${viewOrder.delivery_address.pincode}` : ""}
                </Typography>
                {viewOrder.delivery_address?.delivery_time && (
                  <Typography variant="body2" sx={{ color: "#1565C0" }}>
                    Time: {viewOrder.delivery_address.delivery_time}
                  </Typography>
                )}
                {viewOrder.notes && (
                  <Typography variant="body2" color="text.secondary">Note: {viewOrder.notes}</Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                  Items ({viewOrder.items?.length || 0})
                </Typography>
                {(viewOrder.items || []).map(i => (
                  <Box
                    key={i.grocery_id}
                    sx={{ display: "flex", justifyContent: "space-between", py: 0.8, borderBottom: `1px solid ${BORDER}` }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{i.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {i.quantity} × {money(i.unit_price)}
                        {i.pack_size ? ` · ${i.pack_size}` : ""} · {i.grocery_id}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700}>{money(i.line_total)}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ bgcolor: "#F7FAFA", border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.5 }}>
                <Line l="MRP total" r={money(viewOrder.mrp_total)} />
                <Line l="Subtotal (incl. GST)" r={money(viewOrder.subtotal)} />
                {viewOrder.promo_discount > 0 && (
                  <Line
                    l={`Promo${viewOrder.promo_code ? ` (${viewOrder.promo_code})` : ""}`}
                    r={`-${money(viewOrder.promo_discount)}`}
                    color="#B45309"
                  />
                )}
                <Line l="Delivery" r={viewOrder.delivery_charge > 0 ? money(viewOrder.delivery_charge) : "FREE"} />
                {viewOrder.wallet_deduction > 0 && (
                  <Line
                    l={`Wallet (${viewOrder.wallet_points_used} coins)`}
                    r={`-${money(viewOrder.wallet_deduction)}`}
                    color={PRIMARY}
                  />
                )}
                <Divider sx={{ my: 1 }} />
                <Line
                  l={viewOrder.payment?.method === "offline" ? "To collect (COD)" : "Paid online"}
                  r={money(viewOrder.total_amount)}
                  bold
                />
              </Box>

              {viewOrder.payment && (
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 0.5 }}>Payment</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Method: {viewOrder.payment.method === "offline" ? "Cash on delivery" : "Online (Razorpay)"}
                    {viewOrder.payment.payment_id && viewOrder.payment.method === "online"
                      ? ` · ${viewOrder.payment.payment_id}`
                      : ""}
                  </Typography>
                  {viewOrder.payment.refund_id && (
                    <Typography variant="body2" color="text.secondary">
                      Refund: {viewOrder.payment.refund_id} · {money(viewOrder.payment.refund_amount)} ·{" "}
                      {viewOrder.payment.refund_status}
                    </Typography>
                  )}
                  {viewOrder.payment.refund_error && needsRefund(viewOrder) && (
                    <Typography variant="body2" sx={{ color: "#C62828" }}>
                      Refund error: {viewOrder.payment.refund_error}
                    </Typography>
                  )}
                  {viewOrder.cancel_reason && (
                    <Typography variant="body2" color="text.secondary">
                      Cancel reason: {viewOrder.cancel_reason}
                    </Typography>
                  )}
                </Box>
              )}

              {viewOrder.status_history?.length > 0 && (
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 0.5 }}>History</Typography>
                  {viewOrder.status_history.map((h, idx) => (
                    <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {fmt(h.at)} · {label(h.status)} · {h.by}
                      {h.note ? ` · ${h.note}` : ""}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button onClick={() => setViewOrder(null)}>Close</Button>
          {viewOrder && <ActionButtons order={viewOrder} dense />}
        </DialogActions>
      </Dialog>


      {/* CANCEL REASON */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, order: null, reason: "" })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancel order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Cancel <strong>{cancelDialog.order?.order_id}</strong>? Stock will be restored
            {cancelDialog.order?.payment_status === "paid" && cancelDialog.order?.payment?.method === "online"
              ? " and the online payment will be refunded"
              : ""}
            .
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={cancelDialog.reason}
            onChange={e => setCancelDialog(prev => ({ ...prev, reason: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, order: null, reason: "" })}>Keep order</Button>
          <Button variant="contained" color="error" onClick={confirmCancel}>
            Cancel order
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default GroceryOrdersTab;